import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Tesseract from 'tesseract.js';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Camera, 
  ScanLine, 
  Keyboard, 
  Image as ImageIcon, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Trash2,
  User,
  CreditCard,
  Smartphone,
  AlertCircle
} from 'lucide-react';

const QatarIDScanner = ({ 
  isOpen, 
  onClose, 
  onScanComplete, 
  onLookupPatient,
  isRTL = false 
}) => {
  const [scanMode, setScanMode] = useState('select'); // 'select', 'barcode', 'ocr', 'usb'
  const [isScanning, setIsScanning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [capturedImage, setCapturedImage] = useState(null);
  const [usbInput, setUsbInput] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const usbInputRef = useRef(null);
  const streamRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
    };
    checkMobile();
  }, []);

  // Cleanup on unmount or close
  useEffect(() => {
    if (!isOpen) {
      cleanup();
    }
    return () => cleanup();
  }, [isOpen]);

  // Focus USB input when in USB mode
  useEffect(() => {
    if (scanMode === 'usb' && usbInputRef.current) {
      setTimeout(() => usbInputRef.current?.focus(), 100);
    }
  }, [scanMode]);

  const cleanup = () => {
    stopBarcodeScanner();
    stopVideoStream();
    setCameraError(null);
    setCameraReady(false);
  };

  const stopBarcodeScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.log('Scanner cleanup:', err.message);
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const stopVideoStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  };

  // Check camera availability
  const checkCameraAvailability = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(isRTL ? 'الكاميرا غير مدعومة في هذا المتصفح' : 'Camera not supported in this browser');
      }
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      
      if (cameras.length === 0) {
        throw new Error(isRTL ? 'لم يتم العثور على كاميرا' : 'No camera found');
      }
      
      return true;
    } catch (err) {
      console.error('Camera check failed:', err);
      return false;
    }
  };

  // Parse Qatar ID barcode data
  const parseQatarIdBarcode = (data) => {
    let result = { id_number: '', name: '', date_of_birth: '', age: 0 };
    
    try {
      // Try JSON first
      if (data.startsWith('{')) {
        const json = JSON.parse(data);
        result.id_number = json.id || json.ID || json.qid || json.QID || '';
        result.name = json.name || json.NAME || json.fullName || '';
        result.date_of_birth = json.dob || json.DOB || json.dateOfBirth || json.birth_date || '';
        if (result.date_of_birth) {
          result.age = calculateAgeFromDOB(result.date_of_birth);
        }
        return result;
      }
      
      // Check for pipe-separated
      if (data.includes('|')) {
        const parts = data.split('|');
        result.id_number = parts[0]?.trim() || '';
        result.name = parts[1]?.trim() || '';
        if (parts[2]) {
          result.date_of_birth = parts[2].trim();
          result.age = calculateAgeFromDOB(result.date_of_birth);
        }
        return result;
      }
      
      // Check for comma-separated
      if (data.includes(',')) {
        const parts = data.split(',');
        result.id_number = parts[0]?.trim() || '';
        result.name = parts[1]?.trim() || '';
        if (parts[2]) {
          result.date_of_birth = parts[2].trim();
          result.age = calculateAgeFromDOB(result.date_of_birth);
        }
        return result;
      }
      
      // Check if it's just a number (ID only)
      const cleanData = data.replace(/\D/g, '');
      if (cleanData.length >= 10 && cleanData.length <= 12) {
        result.id_number = cleanData;
        return result;
      }
      
      result.id_number = data.trim();
    } catch (err) {
      console.error('Parse error:', err);
      result.id_number = data.trim();
    }
    
    return result;
  };

  // Calculate age from date of birth string
  const calculateAgeFromDOB = (dobStr) => {
    try {
      let dob = null;
      
      // Try various date formats
      // dd/mm/yyyy
      const ddmmyyyy = dobStr.match(/(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/);
      if (ddmmyyyy) {
        const day = parseInt(ddmmyyyy[1]);
        const month = parseInt(ddmmyyyy[2]);
        const year = parseInt(ddmmyyyy[3]);
        if (year > 1900 && year < 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          dob = new Date(year, month - 1, day);
        }
      }
      
      // yyyy-mm-dd
      if (!dob) {
        const yyyymmdd = dobStr.match(/(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})/);
        if (yyyymmdd) {
          const year = parseInt(yyyymmdd[1]);
          const month = parseInt(yyyymmdd[2]);
          const day = parseInt(yyyymmdd[3]);
          if (year > 1900 && year < 2100) {
            dob = new Date(year, month - 1, day);
          }
        }
      }
      
      if (dob && !isNaN(dob.getTime())) {
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        return age > 0 && age < 150 ? age : 0;
      }
    } catch (err) {
      console.log('DOB parse error:', err);
    }
    return 0;
  };

  // Start barcode scanner with mobile support
  const startBarcodeScanner = async () => {
    setScanMode('barcode');
    setCameraError(null);
    setScanResult(null);
    
    const hasCamera = await checkCameraAvailability();
    if (!hasCamera) {
      setCameraError(isRTL ? 'الكاميرا غير متاحة' : 'Camera not available');
      return;
    }

    // Small delay to ensure DOM is ready
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      html5QrCodeRef.current = new Html5Qrcode('qr-reader', { verbose: false });
      
      // Mobile-optimized config
      const config = {
        fps: 10,
        qrbox: isMobile ? { width: 200, height: 120 } : { width: 250, height: 150 },
        aspectRatio: isMobile ? 1.0 : 1.5,
        disableFlip: false,
        // Important for mobile
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };
      
      // Use environment camera (back camera) for mobile
      const cameraConfig = { facingMode: 'environment' };
      
      await html5QrCodeRef.current.start(
        cameraConfig,
        config,
        async (decodedText) => {
          console.log('Scanned:', decodedText);
          
          // Vibrate on success (mobile)
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          
          await stopBarcodeScanner();
          
          const parsed = parseQatarIdBarcode(decodedText);
          
          // Lookup patient if only ID
          if (parsed.id_number && !parsed.name && onLookupPatient) {
            setIsProcessing(true);
            try {
              const patient = await onLookupPatient(parsed.id_number);
              if (patient && patient.found) {
                parsed.name = patient.patient_name;
              }
            } catch (err) {
              console.log('Lookup failed:', err);
            }
            setIsProcessing(false);
          }
          
          setPreviewData(parsed);
          setScanResult('success');
        },
        (errorMessage) => {
          // Scanning continues - no action needed
        }
      );
      
      setIsScanning(true);
      
    } catch (err) {
      console.error('Scanner start error:', err);
      let errorMsg = isRTL ? 'فشل في تشغيل الكاميرا' : 'Failed to start camera';
      
      if (err.message?.includes('Permission')) {
        errorMsg = isRTL 
          ? 'يرجى السماح بالوصول للكاميرا من إعدادات المتصفح' 
          : 'Please allow camera access in browser settings';
      } else if (err.message?.includes('NotFound')) {
        errorMsg = isRTL ? 'لم يتم العثور على كاميرا' : 'No camera found';
      } else if (err.message?.includes('NotAllowed')) {
        errorMsg = isRTL 
          ? 'تم رفض الوصول للكاميرا. يرجى السماح من الإعدادات' 
          : 'Camera access denied. Please allow in settings';
      }
      
      setCameraError(errorMsg);
      setScanMode('select');
    }
  };

  // Handle USB barcode input
  const handleUsbInput = async (e) => {
    if (e.key === 'Enter' && usbInput.trim()) {
      setIsProcessing(true);
      
      const parsed = parseQatarIdBarcode(usbInput.trim());
      
      if (parsed.id_number && !parsed.name && onLookupPatient) {
        try {
          const patient = await onLookupPatient(parsed.id_number);
          if (patient && patient.found) {
            parsed.name = patient.patient_name;
          }
        } catch (err) {
          console.log('Lookup failed:', err);
        }
      }
      
      setPreviewData(parsed);
      setScanResult('success');
      setIsProcessing(false);
      setUsbInput('');
    }
  };

  // Start OCR camera with full mobile support
  const startOCRCamera = async () => {
    setScanMode('ocr');
    setCapturedImage(null);
    setScanResult(null);
    setCameraError(null);
    setCameraReady(false);
    
    const hasCamera = await checkCameraAvailability();
    if (!hasCamera) {
      setCameraError(isRTL ? 'الكاميرا غير متاحة' : 'Camera not available');
      return;
    }

    // Small delay for DOM
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Mobile-optimized constraints
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: isMobile ? 1280 : 1920 },
          height: { ideal: isMobile ? 720 : 1080 },
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // iOS Safari requires these attributes
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');
        videoRef.current.setAttribute('muted', 'true');
        videoRef.current.muted = true;
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => {
              setCameraReady(true);
              setIsScanning(true);
            })
            .catch(err => {
              console.error('Video play error:', err);
              // Try playing on user interaction
              setCameraReady(true);
              setIsScanning(true);
            });
        };
      }
      
    } catch (err) {
      console.error('Camera error:', err);
      let errorMsg = isRTL ? 'فشل في تشغيل الكاميرا' : 'Failed to start camera';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMsg = isRTL 
          ? 'يرجى السماح بالوصول للكاميرا. افتح إعدادات المتصفح واسمح للكاميرا' 
          : 'Please allow camera access. Open browser settings and enable camera';
      } else if (err.name === 'NotFoundError') {
        errorMsg = isRTL ? 'لم يتم العثور على كاميرا' : 'No camera found';
      } else if (err.name === 'NotReadableError') {
        errorMsg = isRTL ? 'الكاميرا قيد الاستخدام من تطبيق آخر' : 'Camera in use by another app';
      }
      
      setCameraError(errorMsg);
    }
  };

  // Capture image for OCR with preprocessing
  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Use actual video dimensions for best quality
    const width = video.videoWidth || video.clientWidth;
    const height = video.videoHeight || video.clientHeight;
    
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);
    
    // Preprocess: increase contrast and convert to grayscale for better OCR
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      // Grayscale
      const gray = 0.299 * data[i] + 0.587 * data[i+1] + 0.114 * data[i+2];
      // Increase contrast
      const contrast = 1.5;
      const factor = (259 * (contrast * 128 + 255)) / (255 * (259 - contrast * 128));
      const newVal = Math.min(255, Math.max(0, factor * (gray - 128) + 128));
      data[i] = data[i+1] = data[i+2] = newVal;
    }
    ctx.putImageData(imgData, 0, 0);
    
    // Apply threshold for cleaner text
    const threshCanvas = document.createElement('canvas');
    threshCanvas.width = width;
    threshCanvas.height = height;
    const threshCtx = threshCanvas.getContext('2d');
    threshCtx.drawImage(canvas, 0, 0);
    
    const imageData = canvas.toDataURL('image/png', 1.0);
    setCapturedImage(imageData);
    
    // Stop video stream
    stopVideoStream();
    setIsScanning(false);
    
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  // Handle file upload for OCR
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(isRTL ? 'يرجى اختيار ملف صورة' : 'Please select an image file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
        setScanMode('ocr');
        setCameraError(null);
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Process OCR - optimized for Qatar ID cards
  const processOCR = async () => {
    if (!capturedImage) return;
    
    setIsProcessing(true);
    setOcrProgress(0);
    
    try {
      // Run OCR with both Arabic and English
      const result = await Tesseract.recognize(
        capturedImage,
        'ara+eng',
        {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setOcrProgress(Math.round(m.progress * 100));
            }
          }
        }
      );
      
      const text = result.data.text;
      console.log('OCR Raw Text:', text);
      
      // Extract Qatar ID fields
      const extracted = extractQatarIDFields(text);
      console.log('Extracted:', extracted);
      
      // Build preview data
      const previewResult = {
        id_number: extracted.id_number || '',
        name: extracted.name_english || extracted.name_arabic || '',
        name_english: extracted.name_english || '',
        name_arabic: extracted.name_arabic || '',
        date_of_birth: extracted.dob || '',
        age: extracted.dob ? calculateAgeFromDOB(extracted.dob) : 0,
        nationality: extracted.nationality || '',
        expiry_date: extracted.expiry_date || '',
        occupation: extracted.occupation || ''
      };
      
      // Lookup patient if we got an ID
      if (previewResult.id_number && !previewResult.name && onLookupPatient) {
        try {
          const patient = await onLookupPatient(previewResult.id_number);
          if (patient && patient.found) {
            previewResult.name = patient.patient_name;
          }
        } catch (err) {
          console.log('Lookup failed:', err);
        }
      }
      
      setPreviewData(previewResult);
      setScanResult(previewResult.id_number ? 'success' : 'partial');
      
      // Clear image for security
      setCapturedImage(null);
      
    } catch (err) {
      console.error('OCR error:', err);
      toast.error(isRTL ? 'فشل في قراءة البطاقة' : 'Failed to read ID card');
      setScanResult('error');
    } finally {
      setIsProcessing(false);
      setOcrProgress(0);
    }
  };

  // Specialized Qatar ID field extractor
  const extractQatarIDFields = (text) => {
    const result = {
      id_number: null,
      name_english: null,
      name_arabic: null,
      dob: null,
      nationality: null,
      expiry_date: null,
      occupation: null
    };
    
    // Normalize text - fix common OCR errors
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const fullText = lines.join('\n');
    
    console.log('OCR Lines:', lines);
    
    // === 1. ID Number (الرقم الشخصي) - 11-digit number ===
    // Qatar ID is always 11 digits, sometimes with spaces from OCR
    const idPatterns = [
      /\b(\d{11})\b/,                          // Clean 11 digits
      /\b(\d{3}[\s]?\d{4}[\s]?\d{4})\b/,      // With spaces: 283 4567 8901
      /(?:I\.?D\.?|QID|الرقم|الشخصي|رقم)[:\s#]*(\d[\d\s]{9,13})/i,
    ];
    
    for (const pattern of idPatterns) {
      const match = fullText.match(pattern);
      if (match) {
        const cleaned = match[1].replace(/\s/g, '');
        if (cleaned.length === 11 && /^\d+$/.test(cleaned)) {
          result.id_number = cleaned;
          break;
        }
      }
    }
    
    // Fallback: find any sequence of 11 digits in the text
    if (!result.id_number) {
      const allDigits = fullText.replace(/[^\d\s]/g, '').replace(/\s+/g, ' ');
      const digitGroups = allDigits.match(/\d{10,12}/g);
      if (digitGroups) {
        for (const group of digitGroups) {
          if (group.length === 11) {
            result.id_number = group;
            break;
          }
        }
      }
    }
    
    // === 2. Dates - D.O.B and Expiry ===
    // Qatar ID dates are typically DD/MM/YYYY or DD-MM-YYYY
    const allDates = [];
    const dateRegex = /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/g;
    let dateMatch;
    while ((dateMatch = dateRegex.exec(fullText)) !== null) {
      const day = parseInt(dateMatch[1]);
      const month = parseInt(dateMatch[2]);
      const year = parseInt(dateMatch[3]);
      if (year >= 1920 && year <= 2040 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        allDates.push({ 
          full: dateMatch[0], 
          day, month, year,
          date: new Date(year, month - 1, day)
        });
      }
    }
    
    // D.O.B patterns - look for labeled date first
    const dobLabelPatterns = [
      /(?:D\.?O\.?B\.?|Date\s*of\s*Birth|تاريخ\s*الميلاد|الميلاد|Birth)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
      /(?:Born|مواليد)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    ];
    
    for (const pattern of dobLabelPatterns) {
      const match = fullText.match(pattern);
      if (match) {
        result.dob = match[1];
        break;
      }
    }
    
    // Expiry date patterns
    const expiryPatterns = [
      /(?:Expiry|Exp\.?|Valid|انتهاء|صلاحية|الصلاحية|ينتهي)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
    ];
    
    for (const pattern of expiryPatterns) {
      const match = fullText.match(pattern);
      if (match) {
        result.expiry_date = match[1];
        break;
      }
    }
    
    // If we found dates but couldn't label them, use heuristics
    if (allDates.length >= 2 && (!result.dob || !result.expiry_date)) {
      // Sort by year - oldest is likely DOB, newest is likely expiry
      allDates.sort((a, b) => a.year - b.year);
      if (!result.dob) {
        const dobCandidate = allDates.find(d => d.year < 2010);
        if (dobCandidate) result.dob = dobCandidate.full;
      }
      if (!result.expiry_date) {
        const expiryCandidate = allDates.find(d => d.year >= 2025);
        if (expiryCandidate) result.expiry_date = expiryCandidate.full;
      }
    } else if (allDates.length === 1 && !result.dob) {
      // Single date found - likely DOB if year < 2010
      if (allDates[0].year < 2010) {
        result.dob = allDates[0].full;
      }
    }
    
    // === 3. Names ===
    // English name patterns
    const englishNamePatterns = [
      /(?:Name|Full\s*Name)[:\s]+([A-Za-z\s]{3,60})/i,
      /(?:Holder|Bearer)[:\s]+([A-Za-z\s]{3,60})/i,
    ];
    
    for (const pattern of englishNamePatterns) {
      const match = fullText.match(pattern);
      if (match && match[1]) {
        const name = match[1].trim().replace(/\s+/g, ' ');
        if (name.split(' ').length >= 2) {
          result.name_english = name;
          break;
        }
      }
    }
    
    // Fallback: find English name by looking for lines with multiple capitalized words
    if (!result.name_english) {
      for (const line of lines) {
        const cleaned = line.replace(/[^A-Za-z\s]/g, '').trim();
        const words = cleaned.split(/\s+/).filter(w => w.length >= 2);
        if (words.length >= 2 && words.length <= 8 && words.every(w => /^[A-Z]/.test(w))) {
          // Skip common non-name labels
          const skipWords = ['STATE', 'QATAR', 'IDENTITY', 'CARD', 'RESIDENT', 'PERMIT', 'NATIONALITY', 'OCCUPATION', 'NAME', 'DATE', 'BIRTH', 'EXPIRY'];
          if (!words.some(w => skipWords.includes(w.toUpperCase()))) {
            result.name_english = words.join(' ');
            break;
          }
        }
      }
    }
    
    // Arabic name patterns
    const arabicNamePatterns = [
      /(?:الاسم|اسم)[:\s]+([\u0600-\u06FF\s]{4,60})/,
      /(?:صاحب|حامل)[:\s]+([\u0600-\u06FF\s]{4,60})/,
    ];
    
    for (const pattern of arabicNamePatterns) {
      const match = fullText.match(pattern);
      if (match && match[1]) {
        const name = match[1].trim().replace(/\s+/g, ' ');
        if (name.length >= 4) {
          result.name_arabic = name;
          break;
        }
      }
    }
    
    // Fallback: find Arabic name by looking for lines with Arabic text
    if (!result.name_arabic) {
      for (const line of lines) {
        const arabicOnly = line.replace(/[^\u0600-\u06FF\s]/g, '').trim();
        const words = arabicOnly.split(/\s+/).filter(w => w.length >= 2);
        if (words.length >= 2 && words.length <= 8 && arabicOnly.length >= 6) {
          // Skip common labels
          const skipArabic = ['دولة', 'قطر', 'هوية', 'بطاقة', 'إقامة', 'تصريح', 'الجنسية', 'المهنة', 'الاسم', 'تاريخ', 'الميلاد', 'انتهاء', 'الرقم', 'الشخصي'];
          if (!skipArabic.some(s => arabicOnly.includes(s))) {
            result.name_arabic = arabicOnly;
            break;
          }
        }
      }
    }
    
    // === 4. Nationality ===
    const nationalityPatterns = [
      /(?:Nationality|الجنسية)[:\s]+([A-Za-z\s]{3,30})/i,
      /(?:Nationality|الجنسية)[:\s]+([\u0600-\u06FF\s]{3,30})/i,
    ];
    
    for (const pattern of nationalityPatterns) {
      const match = fullText.match(pattern);
      if (match && match[1]) {
        result.nationality = match[1].trim();
        break;
      }
    }
    
    // === 5. Occupation ===
    const occupationPatterns = [
      /(?:Occupation|Profession|المهنة|الوظيفة)[:\s]+([A-Za-z\s]{3,40})/i,
      /(?:Occupation|Profession|المهنة|الوظيفة)[:\s]+([\u0600-\u06FF\s]{3,40})/i,
    ];
    
    for (const pattern of occupationPatterns) {
      const match = fullText.match(pattern);
      if (match && match[1]) {
        result.occupation = match[1].trim();
        break;
      }
    }
    
    return result;
  };

  // Confirm and apply scanned data
  const confirmAndApply = () => {
    if (previewData) {
      // Normalize: use English name as primary, fallback to Arabic
      const finalData = {
        ...previewData,
        name: previewData.name_english || previewData.name || previewData.name_arabic || ''
      };
      onScanComplete(finalData);
      handleClose();
    }
  };

  // Handle close and cleanup
  const handleClose = () => {
    cleanup();
    setCapturedImage(null);
    setScanResult(null);
    setPreviewData(null);
    setScanMode('select');
    setUsbInput('');
    setCameraError(null);
    onClose();
  };

  // Reset to try again
  const resetScanner = () => {
    cleanup();
    setCapturedImage(null);
    setScanResult(null);
    setPreviewData(null);
    setScanMode('select');
    setUsbInput('');
    setCameraError(null);
  };

  // Get status badge
  const getStatusBadge = () => {
    if (scanResult === 'success') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 gap-1 px-3 py-1">
          <CheckCircle2 className="h-4 w-4" />
          {isRTL ? 'تم المسح بنجاح' : 'Scan Successful'}
        </Badge>
      );
    } else if (scanResult === 'partial') {
      return (
        <Badge className="bg-amber-100 text-amber-700 gap-1 px-3 py-1">
          <AlertTriangle className="h-4 w-4" />
          {isRTL ? 'بيانات جزئية' : 'Partial Data'}
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-700 gap-1 px-3 py-1">
        <XCircle className="h-4 w-4" />
        {isRTL ? 'فشل المسح' : 'Scan Failed'}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-sky-500" />
            {isRTL ? 'مسح بطاقة قطر الشخصية' : 'Qatar ID Card Scan'}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {isMobile && <Smartphone className="h-4 w-4 text-emerald-500" />}
            {isRTL 
              ? 'اختر طريقة المسح لاستخراج البيانات تلقائياً'
              : 'Choose a scan method to automatically extract data'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera Error Display */}
          {cameraError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-700 font-medium text-sm">{cameraError}</p>
                  <p className="text-red-600 text-xs mt-1">
                    {isRTL 
                      ? 'يمكنك استخدام خيار رفع صورة أو الإدخال اليدوي'
                      : 'You can use Upload Image or manual entry'
                    }
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetScanner}
                className="mt-3 w-full"
              >
                {isRTL ? 'العودة للخيارات' : 'Back to Options'}
              </Button>
            </div>
          )}

          {/* Mode Selection */}
          {scanMode === 'select' && !scanResult && !cameraError && (
            <div className="grid grid-cols-1 gap-3">
              {/* Barcode/QR Scanner */}
              <button
                onClick={startBarcodeScanner}
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 transition-all text-start active:scale-98"
              >
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                  <ScanLine className="h-6 w-6 text-sky-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">
                    {isRTL ? 'مسح الباركود / QR' : 'Barcode / QR Scan'}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">
                    {isRTL ? 'استخدم الكاميرا لمسح الباركود' : 'Use camera to scan barcode'}
                  </p>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 flex-shrink-0">
                  {isRTL ? 'مُفضّل' : 'Best'}
                </Badge>
              </button>

              {/* OCR Camera */}
              <button
                onClick={startOCRCamera}
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 transition-all text-start active:scale-98"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Camera className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">
                    {isRTL ? 'تصوير البطاقة (OCR)' : 'Photo ID (OCR)'}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">
                    {isRTL ? 'التقط صورة لاستخراج البيانات' : 'Capture photo to extract data'}
                  </p>
                </div>
              </button>

              {/* Upload Image */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 transition-all text-start active:scale-98"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <ImageIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">
                    {isRTL ? 'رفع صورة البطاقة' : 'Upload ID Image'}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">
                    {isRTL ? 'اختر صورة من المعرض' : 'Select image from gallery'}
                  </p>
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileUpload}
              />

              {/* USB Scanner - Hide on mobile */}
              {!isMobile && (
                <button
                  onClick={() => setScanMode('usb')}
                  className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 transition-all text-start"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Keyboard className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">
                      {isRTL ? 'قارئ باركود USB' : 'USB Scanner'}
                    </h3>
                    <p className="text-sm text-slate-500 truncate">
                      {isRTL ? 'استخدم قارئ باركود متصل' : 'Use connected scanner'}
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Barcode Scanner View */}
          {scanMode === 'barcode' && !cameraError && (
            <div className="space-y-3">
              <div 
                id="qr-reader" 
                ref={scannerRef}
                className="rounded-xl overflow-hidden bg-black"
                style={{ minHeight: isMobile ? '280px' : '300px' }}
              />
              {isScanning && (
                <div className="flex items-center justify-center gap-2 text-sky-600">
                  <div className="h-2 w-2 bg-sky-500 rounded-full animate-pulse" />
                  <span className="text-sm">
                    {isRTL ? 'وجّه الكاميرا نحو الباركود...' : 'Point camera at barcode...'}
                  </span>
                </div>
              )}
              <Button 
                variant="outline" 
                onClick={resetScanner} 
                className="w-full"
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          )}

          {/* USB Scanner Input */}
          {scanMode === 'usb' && !scanResult && (
            <div className="space-y-4">
              <div className="p-6 bg-indigo-50 rounded-xl border-2 border-dashed border-indigo-200 text-center">
                <Keyboard className="h-12 w-12 text-indigo-400 mx-auto mb-3" />
                <p className="text-indigo-700 font-medium mb-2">
                  {isRTL ? 'امسح باركود البطاقة الآن...' : 'Scan ID card barcode now...'}
                </p>
                <Input
                  ref={usbInputRef}
                  value={usbInput}
                  onChange={(e) => setUsbInput(e.target.value)}
                  onKeyDown={handleUsbInput}
                  placeholder={isRTL ? 'أو أدخل الرقم يدوياً' : 'Or enter number manually'}
                  className="text-center bg-white"
                  autoFocus
                />
              </div>
              <Button variant="outline" onClick={resetScanner} className="w-full">
                {isRTL ? 'رجوع' : 'Back'}
              </Button>
            </div>
          )}

          {/* OCR Camera View */}
          {scanMode === 'ocr' && !capturedImage && !cameraError && (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden bg-black" style={{ minHeight: '250px' }}>
                <video 
                  ref={videoRef} 
                  className="w-full h-full object-cover"
                  style={{ minHeight: '250px' }}
                  playsInline
                  webkit-playsinline="true"
                  muted
                  autoPlay
                />
                {/* Guide frame */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-4 md:inset-8 border-2 border-dashed border-white/60 rounded-lg" />
                  <div className="absolute bottom-2 left-0 right-0 text-center">
                    <span className="bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {isRTL ? 'ضع البطاقة داخل الإطار' : 'Position ID within frame'}
                    </span>
                  </div>
                </div>
                
                {/* Loading indicator */}
                {!cameraReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-white mx-auto mb-2" />
                      <p className="text-white text-sm">{isRTL ? 'جاري تشغيل الكاميرا...' : 'Starting camera...'}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetScanner} className="flex-1">
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button 
                  onClick={captureImage}
                  disabled={!cameraReady}
                  className="flex-1 bg-sky-500 hover:bg-sky-600 gap-2"
                >
                  <Camera className="h-4 w-4" />
                  {isRTL ? 'التقاط' : 'Capture'}
                </Button>
              </div>
            </div>
          )}

          {/* Captured Image Preview */}
          {scanMode === 'ocr' && capturedImage && !scanResult && (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden">
                <img src={capturedImage} alt="Captured ID" className="w-full rounded-xl" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setCapturedImage(null); startOCRCamera(); }}
                  className="absolute top-2 end-2 bg-black/50 hover:bg-black/70 text-white"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-sky-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{isRTL ? 'جاري قراءة البطاقة...' : 'Reading ID card...'} {ocrProgress}%</span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-sky-500 transition-all duration-300"
                      style={{ width: `${ocrProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {!isProcessing && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setCapturedImage(null); startOCRCamera(); }}
                    className="flex-1 gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    {isRTL ? 'إعادة' : 'Retake'}
                  </Button>
                  <Button 
                    onClick={processOCR}
                    className="flex-1 bg-sky-500 hover:bg-sky-600 gap-2"
                  >
                    <ScanLine className="h-4 w-4" />
                    {isRTL ? 'استخراج' : 'Extract'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Processing Indicator */}
          {isProcessing && scanMode !== 'ocr' && (
            <div className="flex items-center justify-center gap-3 py-8">
              <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
              <span className="text-slate-600">
                {isRTL ? 'جاري البحث...' : 'Searching...'}
              </span>
            </div>
          )}

          {/* Result Preview */}
          {scanResult && previewData && (
            <div className="space-y-4">
              <div className="flex justify-center">{getStatusBadge()}</div>

              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {isRTL ? 'البيانات المستخرجة من البطاقة' : 'Extracted QID Data'}
                </h4>
                
                <div className="space-y-2">
                  {/* ID Number */}
                  <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                    <span className="text-sm text-slate-500">{isRTL ? 'رقم البطاقة' : 'ID Number'}</span>
                    <span className="font-mono font-medium text-indigo-700">
                      {previewData.id_number || <span className="text-slate-400 italic">{isRTL ? 'غير موجود' : 'Not found'}</span>}
                    </span>
                  </div>
                  
                  {/* English Name */}
                  <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                    <span className="text-sm text-slate-500">{isRTL ? 'الاسم (إنجليزي)' : 'Name (English)'}</span>
                    <span className="font-medium">
                      {previewData.name_english || previewData.name || <span className="text-slate-400 italic">{isRTL ? 'غير موجود' : 'Not found'}</span>}
                    </span>
                  </div>

                  {/* Arabic Name */}
                  {previewData.name_arabic && (
                    <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                      <span className="text-sm text-slate-500">{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</span>
                      <span className="font-medium" dir="rtl">{previewData.name_arabic}</span>
                    </div>
                  )}

                  {/* DOB & Age */}
                  {(previewData.date_of_birth || previewData.age > 0) && (
                    <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <span className="text-sm text-emerald-600">{isRTL ? 'تاريخ الميلاد / العمر' : 'D.O.B / Age'}</span>
                      <span className="font-medium text-emerald-700">
                        {previewData.date_of_birth && <span className="me-2">{previewData.date_of_birth}</span>}
                        {previewData.age > 0 && <span>({previewData.age} {isRTL ? 'سنة' : 'yrs'})</span>}
                      </span>
                    </div>
                  )}

                  {/* Nationality */}
                  {previewData.nationality && (
                    <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                      <span className="text-sm text-slate-500">{isRTL ? 'الجنسية' : 'Nationality'}</span>
                      <span className="font-medium">{previewData.nationality}</span>
                    </div>
                  )}

                  {/* Expiry Date */}
                  {previewData.expiry_date && (
                    <div className="flex justify-between items-center p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <span className="text-sm text-amber-600">{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</span>
                      <span className="font-medium text-amber-700">{previewData.expiry_date}</span>
                    </div>
                  )}

                  {/* Occupation */}
                  {previewData.occupation && (
                    <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                      <span className="text-sm text-slate-500">{isRTL ? 'المهنة' : 'Occupation'}</span>
                      <span className="font-medium">{previewData.occupation}</span>
                    </div>
                  )}
                </div>

                {/* Editable fields */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs text-slate-500">{isRTL ? 'تعديل قبل التأكيد:' : 'Edit before confirming:'}</p>
                  <Input
                    value={previewData.id_number}
                    onChange={(e) => setPreviewData({...previewData, id_number: e.target.value})}
                    placeholder={isRTL ? 'رقم البطاقة' : 'ID Number'}
                  />
                  <Input
                    value={previewData.name_english || previewData.name}
                    onChange={(e) => setPreviewData({...previewData, name: e.target.value, name_english: e.target.value})}
                    placeholder={isRTL ? 'الاسم (إنجليزي)' : 'Name (English)'}
                  />
                  <Input
                    value={previewData.date_of_birth || ''}
                    onChange={(e) => {
                      const dob = e.target.value;
                      const age = calculateAgeFromDOB(dob);
                      setPreviewData({...previewData, date_of_birth: dob, age});
                    }}
                    placeholder={isRTL ? 'تاريخ الميلاد (DD/MM/YYYY)' : 'D.O.B (DD/MM/YYYY)'}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={resetScanner} className="flex-1 gap-2">
                  <RefreshCw className="h-4 w-4" />
                  {isRTL ? 'مسح جديد' : 'Scan Again'}
                </Button>
                <Button 
                  onClick={confirmAndApply}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 gap-2"
                  disabled={!previewData.id_number}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {isRTL ? 'تأكيد' : 'Confirm'}
                </Button>
              </div>
            </div>
          )}

          {/* Scan Error with Manual Entry Option */}
          {scanResult === 'error' && (
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-red-700 text-sm mb-3">
                {isRTL ? 'تعذر قراءة البطاقة. يرجى المحاولة مرة أخرى أو الإدخال يدوياً.' : 'Could not read ID. Please try again or enter manually.'}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetScanner} className="flex-1">
                  {isRTL ? 'محاولة أخرى' : 'Try Again'}
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  {isRTL ? 'إدخال يدوي' : 'Manual Entry'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Hidden canvas */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Security Notice */}
        <div className="text-xs text-slate-400 text-center mt-2">
          🔒 {isRTL ? 'لا يتم حفظ صور البطاقات' : 'ID images are not stored'}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QatarIDScanner;
